import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { uploadCaseStudy } from "../../http";
import { getTicket } from "../../pages/actions/actions";
import { ShowAlert } from "../../utils/showAlert";

export const useCaseStudyUpload = (ticketId: string) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();

  const handleSelectFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const selected = ev.target.files?.[0];
    
    if (selected) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg", 
        "image/png",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ];
      
      if (!allowedTypes.includes(selected.type)) {
        ShowAlert("warning", "Only JPG, PNG, PDF, DOC, and DOCX files are allowed!");
        ev.target.value = "";
        setFile(null);
        setFileName("");
        return;
      }
      
      setFile(selected);
      setFileName(selected.name);
    }
  };

  const handleUpload = async () => {
    if (!file) return alert("Please select a file");

    try {
      await uploadCaseStudy(file, ticketId);
      ShowAlert("success", "Case study uploaded successfully");
      setFile(null);
      setFileName("");
      
      // Invalidate React Query cache to trigger refetch
      const ticketIdToUse = id || ticketId;
      queryClient.invalidateQueries({
        queryKey: ["ticket", ticketIdToUse],
      });
      
      // Add a small delay to ensure backend has processed the upload
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh ticket data to get updated case study information
    
      await getTicket({ ticketId: ticketIdToUse, navigate, dispatch });
      
    } catch (err) {
      ShowAlert("Upload failed");
    }
  };

  return {
    file,
    fileName,
    handleSelectFile,
    handleUpload,
  };
};
 